import { LanguageCode } from 'common/src/vtj/LanguageCode.enum'
import {
  Asuinvaltio,
  AvioliittojenLukumaara,
  Etunimet,
  Kansalaisuus,
  Puhelinnumero,
  Sahkopostiosoite,
  Siviilisaaty,
  Sukunimi,
  Syntymapaikka,
  Syntymapaiva,
  Syntymavaltio,
  TilaustenMaara,
  UskonnollinenYhdyskunta,
} from 'todistukset-common/src/vtj/types/editable-field.type'

export enum TodistusPersonRole {
  TILAAJA = 'TILAAJA',
  KOHDEHENKILO = 'KOHDEHENKILO',
  HUOLLETTAVA = 'HUOLLETTAVA',
  PUOLISO_A = 'PUOLISO_A',
  PUOLISO_B = 'PUOLISO_B',
  PUOLESTA_ASIOITAVA = 'PUOLESTA_ASIOITAVA',
  KUOLINPESA = 'KUOLINPESA',
  EDUNVALVONTA_PAAMIES = 'EDUNVALVONTA_PAAMIES',
  /**
   * Elsa virkailija-avaus does not support limit for persons so virkailija can add multiple yritys persons.
   * We store those to todistukset DB but there is no logic for those and todistukset Elsa view does not show those.
   *
   * This is done to avoid failing change events which would generate manual work for the Elsa team.
   * In addition this ensures that asia and todistukset DBs are in sync regarding of persons.
   */
  YLIMAARAINEN_YRITYS = 'YLIMAARAINEN_YRITYS',
}

export enum TodistusPersonHetuSource {
  TUNNISTUS = 'TUNNISTUS',
  ASIAKAS = 'ASIAKAS',
  VIRKAILIJA = 'VIRKAILIJA',
}

export type TodistusAsiointikieli =
  | LanguageCode.FI
  | LanguageCode.SV
  | LanguageCode.EN

export enum TodistusPersonType {
  IHMINEN = 'IHMINEN',
  YRITYS = 'YRITYS',
}

export interface TodistusPerson {
  personId: number
  personType: TodistusPersonType
  requestId: number
  subjectId: number | null
  asiaPersonId: number | null
  hetu: string | null
  hetuSource: TodistusPersonHetuSource | null
  eidasPID: string | null
  birthdate: Syntymapaiva | null
  firstNames: Etunimet | null
  lastName: Sukunimi | null
  email: Sahkopostiosoite | null
  phoneNumber: Puhelinnumero | null
  roles: TodistusPersonRole[]
  maritalStatus: Siviilisaaty | null
  numberOfMarriages: AvioliittojenLukumaara | null
  countryOfResidenceCode: Asuinvaltio | null
  citizenshipCode: Kansalaisuus | null
  religiousCommunityTranslated: UskonnollinenYhdyskunta[]
  kotikuntaKoodi: string | null
  updatedAt: Date
  createdAt: Date
  isDependant: boolean
  orderedAmount: TilaustenMaara | null
  birthPlace: Syntymapaikka | null
  birthCountryCode: Syntymavaltio | null
  asiointikieli: TodistusAsiointikieli | null
  yTunnus: string | null
  organizationName: string | null
  /**
   * suomifiViestiSentAt is not currently used. Database still has this field with data related to previous orders when messages were sent.
   * When implementing suomi.fi messages sending consider this
   */
  suomifiViestiSentAt?: Date | null
}

export interface EidasTodistusPerson extends TodistusPerson {
  personType: TodistusPersonType.IHMINEN
  eidasPID: string
}

export interface HetuTodistusPerson extends TodistusPerson {
  personType: TodistusPersonType.IHMINEN
  hetu: string
}

export interface YritysTodistusPerson extends TodistusPerson {
  personType: TodistusPersonType.YRITYS
  yTunnus: string
}

export interface VirkailijaAvausTodistusPerson extends TodistusPerson {
  eidasPID: null
  hetuSource: TodistusPersonHetuSource.VIRKAILIJA | null
}

export function isHetuTodistusPerson(
  person: TodistusPerson
): person is HetuTodistusPerson {
  return person.hetu !== null
}

export function isHetuTodistusPersonOrIsVirkailijaAvausTodistusPerson(
  person: TodistusPerson
): person is HetuTodistusPerson | VirkailijaAvausTodistusPerson {
  return person.eidasPID === null
}

type Unpersisted<Person> = Omit<
  Person,
  | 'requestId'
  | 'createdAt'
  | 'updatedAt'
  | 'personId'
  | 'subjectId'
  | 'suomifiViestiSentAt'
  | 'asiaPersonId'
> & { asiaPersonId?: number | null }

export type UnpersistedTodistusPerson = Unpersisted<TodistusPerson>

export type UnpersistedEidasTodistusPerson = Unpersisted<EidasTodistusPerson>

export type UnpersistedHetuTodistusPerson = Unpersisted<HetuTodistusPerson>
